import type { ParseRoute } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import type { ColumnDef, ExpandedState, OnChangeFn, Row, SortingState, Updater } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { ReactNode, RefObject } from "react"
import type { routeTree } from "routeTree.gen"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"

interface Props<T extends object> {
  data: T[]
  isLoading: boolean
  expandedState?: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  getRowId: (row: T) => string
  onExpandedChange?: (updaterOrValue: Updater<ExpandedState>) => void
  onSortingChange: OnChangeFn<SortingState>
  onScroll: () => void
  renderExpandedContent: (row: Row<T>) => ReactNode
  columns: ColumnDef<T>[]
  enableRowClick?: boolean
  noDataMessage?: string
  className?: string
  getRoutePath?: (...args: any[]) => ParseRoute<typeof routeTree>["fullPath"]
  routePath?: ParseRoute<typeof routeTree>["fullPath"]
}

export const BaseGenericTable = <T extends object>({
  data,
  isLoading,
  expandedState,
  sortingState,
  tableContainerRef,
  getRowId,
  onExpandedChange,
  onSortingChange,
  onScroll,
  renderExpandedContent,
  columns,
  enableRowClick = true,
  noDataMessage = "No data found",
  className,
  getRoutePath,
  routePath,
}: Props<T>): JSX.Element => {
  const navigate = useNavigate()

  const handleRowClick = (row: Row<T>) => {
    if (!enableRowClick) return

    navigate({ to: getRoutePath?.(row.original) ?? routePath })
  }

  return (
    <AerosBaseTable<T>
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          manualSorting: true,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId,
          state: {
            isLoading,
            expanded: expandedState,
            sorting: sortingState,
          },
          onExpandedChange,
          onSortingChange,
          getRowCanExpand: () => true,
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: tableContainerRef,
          onScroll,
          className,
        },
        bodyCellExpanded: { className: "bg-primary-100" },
        bodyRow: {
          className: enableRowClick ? "hover:bg-primary-100 cursor-pointer" : undefined,
          onClick: enableRowClick ? (value) => handleRowClick(value as Row<T>) : undefined,
        },
      }}
      noDataMessage={noDataMessage}
    />
  )
}
